import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  CREATE_CUSTOM_POPUP,
  DELETE_CUSTOM_POPUP,
  GET_CUSTOM_POPUP_ASSETS,
  GET_CUSTOM_POPUPS,
  UPDATE_CUSTOM_POPUP,
  UPLOAD_CUSTOM_POPUP_ASSET,
} from './graphql';

type LocaleTextMap = Record<string, string>;
type PopupPlacement = 'MAIN_MENU' | 'SHOP' | 'PROFILE' | 'SETTINGS';
type ButtonAction = 'DISMISS' | 'OPEN_URL';
type PayerStatus = 'ALL' | 'PAYER' | 'NON_PAYER';
type VipStatus = 'ALL' | 'VIP' | 'NON_VIP';
type DontShowAgainPolicy = 'SESSION' | 'DAY' | 'FOREVER' | 'NONE';
type PopupUiTheme = 'DEFAULT' | 'VINYL';
type PopupPlatform = 'ALL' | 'IOS' | 'ANDROID';
type MatcherConditionOp = '<=' | '>=' | '==' | '!=' | '<' | '>';
type MatcherCondition = { op: MatcherConditionOp; value: string | number };

type CustomPopupAsset = {
  url: string;
  filename: string;
  updatedAt?: string | null;
};

type CustomPopupConfig = {
  id: string;
  name: string;
  enabled: boolean;
  place: PopupPlacement;
  titleTexts?: LocaleTextMap | null;
  bodyTexts?: LocaleTextMap | null;
  buttonTexts?: LocaleTextMap | null;
  buttonAction: ButtonAction;
  buttonUrl?: string | null;
  isSurveyLink?: boolean | null;
  backgroundImageUrl?: string | null;
  secondaryButtonTexts?: LocaleTextMap | null;
  dontShowAgainTexts?: LocaleTextMap | null;
  dontShowAgainPolicy?: DontShowAgainPolicy | null;
  uiTheme?: PopupUiTheme | null;
  dailyCap: number;
  targetUserIds?: string[] | null;
  platform?: 'IOS' | 'ANDROID' | null;
  minAppVersion?: string | null;
  maxAppVersion?: string | null;
  versionConditions?: MatcherCondition[] | null;
  countries?: string[] | null;
  minLevel?: number | null;
  maxLevel?: number | null;
  levelConditions?: MatcherCondition[] | null;
  payerStatus: PayerStatus;
  vipStatus: VipStatus;
};

const PLACEMENTS: PopupPlacement[] = ['MAIN_MENU', 'SHOP', 'PROFILE', 'SETTINGS'];

const formatJson = (value: LocaleTextMap | null | undefined) => JSON.stringify(value ?? { en: '' }, null, 2);

const parseLocaleTextMap = (value: string, fieldName: string): LocaleTextMap => {
  let parsedValue: unknown;

  try {
    parsedValue = JSON.parse(value);
  } catch {
    throw new Error(`${fieldName} must be valid JSON.`);
  }

  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    throw new Error(`${fieldName} must be a JSON object like {"en":"..."}.`);
  }

  const entries = Object.entries(parsedValue).filter(
    ([locale, text]) => typeof locale === 'string' && typeof text === 'string'
  );

  if (!entries.length) {
    throw new Error(`${fieldName} must contain at least one locale entry.`);
  }

  return Object.fromEntries(entries);
};

const parseOptionalLocaleTextMap = (value: string, fieldName: string): LocaleTextMap | null => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '{}') {
    return null;
  }
  const parsed = parseLocaleTextMap(value, fieldName);
  return Object.values(parsed).some((text) => text.trim()) ? parsed : null;
};

const parseList = (value: string): string[] =>
  value
    .split(/[\s,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const parseOptionalInt = (value: string, fieldName: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error(`${fieldName} must be a positive whole number.`);
  }
  return parsed;
};

const CONDITION_OPS: MatcherConditionOp[] = ['<=', '>=', '==', '!=', '<', '>'];

type ConditionRow = { op: MatcherConditionOp; value: string };

const conditionsToRows = (conditions?: MatcherCondition[] | null): ConditionRow[] =>
  (conditions ?? []).map((condition) => ({ op: condition.op, value: String(condition.value) }));

// Old rows only have min/max values - show them as the equivalent conditions.
const legacyRangeToRows = (min?: string | number | null, max?: string | number | null): ConditionRow[] => {
  const rows: ConditionRow[] = [];
  if (min != null && min !== '') {
    rows.push({ op: '>=', value: String(min) });
  }
  if (max != null && max !== '') {
    rows.push({ op: '<=', value: String(max) });
  }
  return rows;
};

// Empty-value rows are ignored; all remaining conditions must match (AND).
const rowsToConditions = (
  rows: ConditionRow[],
  fieldName: string,
  valueKind: 'version' | 'level'
): MatcherCondition[] =>
  rows
    .filter((row) => row.value.trim())
    .map((row) => {
      const raw = row.value.trim();
      if (valueKind === 'level') {
        const parsed = Number(raw);
        if (!Number.isInteger(parsed) || parsed < 0) {
          throw new Error(`${fieldName}: "${raw}" must be a positive whole number.`);
        }
        return { op: row.op, value: parsed };
      }
      if (!/^[0-9]+(\.[0-9]+)*$/.test(raw)) {
        throw new Error(`${fieldName}: "${raw}" must be a version like 003.013.000.`);
      }
      return { op: row.op, value: raw };
    });

type ConditionListEditorProps = {
  label: string;
  rows: ConditionRow[];
  placeholder: string;
  onChange: (rows: ConditionRow[]) => void;
};

const ConditionListEditor = ({ label, rows, placeholder, onChange }: ConditionListEditorProps) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    {rows.map((row, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={index} className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
        <Form.Control
          as="select"
          style={{ width: 80, flexShrink: 0 }}
          value={row.op}
          onChange={(event) => {
            const next = [...rows];
            next[index] = { ...next[index], op: event.currentTarget.value as MatcherConditionOp };
            onChange(next);
          }}
        >
          {CONDITION_OPS.map((op) => (
            <option key={op} value={op}>
              {op}
            </option>
          ))}
        </Form.Control>
        <Form.Control
          value={row.value}
          placeholder={placeholder}
          onChange={(event) => {
            const next = [...rows];
            next[index] = { ...next[index], value: event.target.value };
            onChange(next);
          }}
        />
        <Button
          size="sm"
          variant="outline-danger"
          onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
        >
          ✕
        </Button>
      </div>
    ))}
    <Button size="sm" variant="outline-primary" onClick={() => onChange([...rows, { op: '>=', value: '' }])}>
      + Add condition
    </Button>
    {rows.length > 1 && <div className="text-muted small mt-1">All conditions must match (AND).</div>}
  </Form.Group>
);

const previewText = (json: string, fallback: string): string => {
  try {
    const parsed = JSON.parse(json);
    const value = parsed?.en ?? Object.values(parsed ?? {})[0];
    return typeof value === 'string' && value.trim() ? value : fallback;
  } catch {
    return fallback;
  }
};

type PopupPreviewProps = {
  titleJson: string;
  bodyJson: string;
  buttonJson: string;
  secondaryJson: string;
  dontShowAgainJson: string;
  dontShowAgainPolicy: DontShowAgainPolicy;
  uiTheme: PopupUiTheme;
  backgroundImageUrl: string;
};

// Mirrors sp3/src/features/popup/CustomPopupModal.js (at ~0.76 scale of the
// in-game card) so admins can see the in-game result without launching the app.
// Keep the layout/colors/scroll behavior in sync with the app.
const PopupPreview = ({
  titleJson,
  bodyJson,
  buttonJson,
  secondaryJson,
  dontShowAgainJson,
  dontShowAgainPolicy,
  uiTheme,
  backgroundImageUrl,
}: PopupPreviewProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ scrollTop: 0, scrollHeight: 0, clientHeight: 0 });

  const bodyText = previewText(bodyJson, 'Body text');

  const measureBody = () => {
    const element = bodyRef.current;
    if (element) {
      setScrollState({
        scrollTop: element.scrollTop,
        scrollHeight: element.scrollHeight,
        clientHeight: element.clientHeight,
      });
    }
  };

  useEffect(measureBody, [bodyText]);

  const { scrollTop, scrollHeight, clientHeight } = scrollState;
  const isScrollable = clientHeight > 0 && scrollHeight > clientHeight + 6;
  const isScrolledToEnd = scrollTop + clientHeight >= scrollHeight - 6;
  const scrollableHeight = Math.max(1, scrollHeight - clientHeight);
  const thumbHeight = Math.min(85, Math.max(36, (clientHeight / Math.max(1, scrollHeight)) * clientHeight * 1.2));
  const thumbTop = Math.max(
    0,
    Math.min((scrollTop / scrollableHeight) * (clientHeight - thumbHeight), clientHeight - thumbHeight)
  );

  const buttonBaseStyle: React.CSSProperties = {
    width: '90%',
    height: 29,
    borderRadius: 13,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 11,
  };

  return (
    <div
      style={{
        background: '#1A1633',
        borderRadius: 12,
        padding: '28px 18px',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <style>{'.custom-popup-preview-body::-webkit-scrollbar{display:none}'}</style>
      <div
        style={{
          width: 260,
          borderRadius: 14,
          border: '3px solid #8A5DE3',
          overflow: 'hidden',
          position: 'relative',
          background: 'linear-gradient(273deg, #8b30d9 0%, #4a0fa3 100%)',
          fontFamily: 'inherit',
        }}
      >
        {backgroundImageUrl.trim() && (
          <>
            <img
              src={backgroundImageUrl.trim()}
              alt=""
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(51,28,76,0.7) 59%, rgba(51,28,76,0.8) 67%, rgba(66,44,111,1) 85%)',
              }}
            />
          </>
        )}
        <div style={{ position: 'relative', padding: '30px 15px 23px', textAlign: 'center', color: 'white' }}>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              lineHeight: '23px',
              maxWidth: '70%',
              margin: '0 auto 11px',
            }}
          >
            {previewText(titleJson, 'Title')}
          </div>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.2)', marginBottom: 11 }} />
          <div style={{ position: 'relative', minHeight: 175, marginBottom: 23 }}>
            <div
              ref={bodyRef}
              className="custom-popup-preview-body"
              onScroll={measureBody}
              style={{
                maxHeight: 152,
                overflowY: 'auto',
                scrollbarWidth: 'none',
                fontSize: 11,
                fontWeight: 700,
                lineHeight: '14px',
                color: 'white',
                padding: '0 17px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {bodyText}
            </div>
            {isScrollable && (
              <div
                style={{
                  position: 'absolute',
                  right: -9,
                  top: thumbTop,
                  width: 3,
                  height: thumbHeight,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.85)',
                }}
              />
            )}
            {isScrollable && !isScrolledToEnd && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 2,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: 2.5,
                    height: 2.5,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.85)',
                    marginBottom: 4.5,
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    style={{
                      width: 7,
                      height: 1.4,
                      borderRadius: 2,
                      background: 'white',
                      transform: 'rotate(48deg)',
                      marginRight: -2.3,
                    }}
                  />
                  <div
                    style={{
                      width: 7,
                      height: 1.4,
                      borderRadius: 2,
                      background: 'white',
                      transform: 'rotate(-48deg)',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div
            style={{
              ...buttonBaseStyle,
              background: 'linear-gradient(90deg, #FF563B 0%, #FF680B 100%)',
            }}
          >
            {previewText(buttonJson, 'OK')}
          </div>
          <div
            style={{
              ...buttonBaseStyle,
              marginTop: 8,
              border: '1.5px solid #D4BEFF',
              color: '#D4BEFF',
            }}
          >
            {previewText(secondaryJson, 'Check later')}
          </div>
          {dontShowAgainPolicy !== 'NONE' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginTop: 11,
                fontSize: 10,
                fontWeight: 700,
                color: '#E3D4FF',
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  border: '1.5px solid #331C4C',
                  background: '#E3D4FF',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              {previewText(dontShowAgainJson, "Don't show again")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Zebra striping for the editor sections: alternating tinted bands so each scope
// is visually separated. The translucent gray works in both light and dark mode.
const sectionStyle = (index: number): React.CSSProperties => ({
  padding: '12px 14px 4px',
  borderRadius: 8,
  marginBottom: 12,
  background: index % 2 === 0 ? 'rgba(127, 127, 127, 0.09)' : 'transparent',
});

const CustomPopups = () => {
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusVariant, setStatusVariant] = useState<'success' | 'danger' | 'info'>('info');
  const [isDraft, setIsDraft] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [place, setPlace] = useState<PopupPlacement>('MAIN_MENU');
  const [titleTextsJson, setTitleTextsJson] = useState(formatJson(null));
  const [bodyTextsJson, setBodyTextsJson] = useState(formatJson(null));
  const [buttonTextsJson, setButtonTextsJson] = useState(formatJson({ en: 'OK' }));
  const [buttonAction, setButtonAction] = useState<ButtonAction>('DISMISS');
  const [buttonUrl, setButtonUrl] = useState('');
  const [isSurveyLink, setIsSurveyLink] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [secondaryButtonTextsJson, setSecondaryButtonTextsJson] = useState(formatJson({ en: 'Check later' }));
  const [dontShowAgainTextsJson, setDontShowAgainTextsJson] = useState(
    formatJson({ en: "Don't show again this session" })
  );
  const [dontShowAgainPolicy, setDontShowAgainPolicy] = useState<DontShowAgainPolicy>('SESSION');
  const [uiTheme, setUiTheme] = useState<PopupUiTheme>('DEFAULT');
  const [dailyCap, setDailyCap] = useState('1');
  const [targetUserIdsText, setTargetUserIdsText] = useState('');
  const [platform, setPlatform] = useState<PopupPlatform>('ALL');
  const [versionConditionRows, setVersionConditionRows] = useState<ConditionRow[]>([]);
  const [countriesText, setCountriesText] = useState('');
  const [levelConditionRows, setLevelConditionRows] = useState<ConditionRow[]>([]);
  const [payerStatus, setPayerStatus] = useState<PayerStatus>('ALL');
  const [vipStatus, setVipStatus] = useState<VipStatus>('ALL');

  const { data, loading, refetch } = useQuery(GET_CUSTOM_POPUPS, {
    fetchPolicy: 'network-only',
  });
  const { data: assetsData, refetch: refetchAssets } = useQuery(GET_CUSTOM_POPUP_ASSETS, {
    skip: !showEditorModal,
    fetchPolicy: 'cache-and-network',
  });
  const [createPopupConfig, { loading: createLoading }] = useMutation(CREATE_CUSTOM_POPUP);
  const [updatePopupConfig, { loading: updateLoading }] = useMutation(UPDATE_CUSTOM_POPUP);
  const [deletePopupConfig, { loading: deleteLoading }] = useMutation(DELETE_CUSTOM_POPUP);
  const [uploadPopupAsset, { loading: uploadLoading }] = useMutation(UPLOAD_CUSTOM_POPUP_ASSET, {
    onCompleted: (result) => {
      const url = result?.uploadCustomPopupAsset?.url;
      if (url) {
        setBackgroundImageUrl(url);
        setStatusVariant('success');
        setStatusMessage('Background image uploaded.');
        refetchAssets();
      }
    },
    onError: (error) => {
      setStatusVariant('danger');
      setStatusMessage(error.message);
    },
  });

  const uploadedAssets: CustomPopupAsset[] = assetsData?.liveOps?.customPopupAssets ?? [];

  const onBackgroundFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      uploadPopupAsset({ variables: { file } });
    }
    // allow re-selecting the same file
    // eslint-disable-next-line no-param-reassign
    event.currentTarget.value = '';
  };

  const popups: CustomPopupConfig[] = useMemo(() => {
    const configs = data?.liveOps?.customPopupConfigs ?? [];
    return [...configs].sort((left: CustomPopupConfig, right: CustomPopupConfig) =>
      left.name.localeCompare(right.name)
    );
  }, [data?.liveOps?.customPopupConfigs]);

  useEffect(() => {
    if (!statusMessage) {
      return undefined;
    }
    const timeout = setTimeout(() => setStatusMessage(null), 6000);
    return () => clearTimeout(timeout);
  }, [statusMessage]);

  const resetForm = (popup?: CustomPopupConfig) => {
    setName(popup?.name ?? '');
    setEnabled(popup?.enabled ?? true);
    setPlace(popup?.place ?? 'MAIN_MENU');
    setTitleTextsJson(formatJson(popup?.titleTexts));
    setBodyTextsJson(formatJson(popup?.bodyTexts));
    setButtonTextsJson(formatJson(popup?.buttonTexts ?? { en: 'OK' }));
    setButtonAction(popup?.buttonAction ?? 'DISMISS');
    setButtonUrl(popup?.buttonUrl ?? '');
    setIsSurveyLink(popup?.isSurveyLink ?? false);
    setBackgroundImageUrl(popup?.backgroundImageUrl ?? '');
    setSecondaryButtonTextsJson(formatJson(popup?.secondaryButtonTexts ?? { en: 'Check later' }));
    setDontShowAgainTextsJson(formatJson(popup?.dontShowAgainTexts ?? { en: "Don't show again this session" }));
    setDontShowAgainPolicy(popup?.dontShowAgainPolicy ?? 'SESSION');
    setUiTheme(popup?.uiTheme ?? 'DEFAULT');
    setDailyCap(String(popup?.dailyCap ?? 1));
    setTargetUserIdsText((popup?.targetUserIds ?? []).join('\n'));
    setPlatform(popup?.platform ?? 'ALL');
    const versionRows = conditionsToRows(popup?.versionConditions);
    setVersionConditionRows(
      versionRows.length ? versionRows : legacyRangeToRows(popup?.minAppVersion, popup?.maxAppVersion)
    );
    setCountriesText((popup?.countries ?? []).join(', '));
    const levelRows = conditionsToRows(popup?.levelConditions);
    setLevelConditionRows(levelRows.length ? levelRows : legacyRangeToRows(popup?.minLevel, popup?.maxLevel));
    setPayerStatus(popup?.payerStatus ?? 'ALL');
    setVipStatus(popup?.vipStatus ?? 'ALL');
  };

  const onNewPopup = () => {
    setIsDraft(true);
    setEditingId(null);
    resetForm();
    setShowEditorModal(true);
  };

  const onEditPopup = (popup: CustomPopupConfig) => {
    setIsDraft(false);
    setEditingId(popup.id);
    resetForm(popup);
    setShowEditorModal(true);
  };

  const onDeletePopup = async (popup: CustomPopupConfig) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete popup "${popup.name}"?`)) {
      return;
    }
    try {
      await deletePopupConfig({ variables: { id: popup.id } });
      setStatusVariant('success');
      setStatusMessage(`Popup "${popup.name}" deleted.`);
      await refetch();
    } catch (error) {
      setStatusVariant('danger');
      setStatusMessage(error instanceof Error ? error.message : 'Failed to delete popup.');
    }
  };

  const buildInput = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error('Name is required.');
    }

    const parsedDailyCap = parseOptionalInt(dailyCap, 'Daily cap');
    if (parsedDailyCap == null || parsedDailyCap < 1) {
      throw new Error('Daily cap must be at least 1.');
    }

    const versionConditions = rowsToConditions(versionConditionRows, 'App version conditions', 'version');
    const levelConditions = rowsToConditions(levelConditionRows, 'Player level conditions', 'level');

    const trimmedButtonUrl = buttonUrl.trim();
    if (buttonAction === 'OPEN_URL' && !trimmedButtonUrl) {
      throw new Error('Button URL is required when the button action is OPEN_URL.');
    }

    return {
      name: trimmedName,
      enabled,
      place,
      titleTexts: parseLocaleTextMap(titleTextsJson, 'Title texts'),
      bodyTexts: parseLocaleTextMap(bodyTextsJson, 'Content texts'),
      buttonTexts: parseLocaleTextMap(buttonTextsJson, 'Button texts'),
      buttonAction,
      buttonUrl: buttonAction === 'OPEN_URL' ? trimmedButtonUrl : null,
      isSurveyLink: buttonAction === 'OPEN_URL' ? isSurveyLink : false,
      backgroundImageUrl: backgroundImageUrl.trim() || null,
      secondaryButtonTexts: parseOptionalLocaleTextMap(secondaryButtonTextsJson, 'Secondary button texts'),
      dontShowAgainTexts: parseOptionalLocaleTextMap(dontShowAgainTextsJson, "Don't show again texts"),
      dontShowAgainPolicy,
      uiTheme,
      dailyCap: parsedDailyCap,
      targetUserIds: parseList(targetUserIdsText),
      platform: platform === 'ALL' ? null : platform,
      minAppVersion: null,
      maxAppVersion: null,
      versionConditions: versionConditions.length ? versionConditions : null,
      countries: parseList(countriesText).map((country) => country.toUpperCase()),
      minLevel: null,
      maxLevel: null,
      levelConditions: levelConditions.length ? levelConditions : null,
      payerStatus,
      vipStatus,
    };
  };

  const onSavePopup = async () => {
    try {
      const input = buildInput();
      if (isDraft) {
        const id = `custom-${slugify(input.name) || 'popup'}-${Date.now()}`;
        await createPopupConfig({ variables: { input: { id, ...input } } });
        setStatusMessage(`Popup "${input.name}" created.`);
      } else {
        if (!editingId) {
          throw new Error('No popup selected.');
        }
        await updatePopupConfig({ variables: { input: { id: editingId, ...input } } });
        setStatusMessage(`Popup "${input.name}" updated.`);
      }
      setStatusVariant('success');
      setShowEditorModal(false);
      await refetch();
    } catch (error) {
      setStatusVariant('danger');
      setStatusMessage(error instanceof Error ? error.message : 'Failed to save popup.');
    }
  };

  const describeMatchers = (popup: CustomPopupConfig) => {
    const matchers: string[] = [];
    if (popup.targetUserIds?.length) {
      matchers.push(`${popup.targetUserIds.length} user(s)`);
    }
    if (popup.platform) {
      matchers.push(popup.platform);
    }
    if (popup.versionConditions?.length) {
      matchers.push(`v ${popup.versionConditions.map((c) => `${c.op}${c.value}`).join(' & ')}`);
    } else if (popup.minAppVersion || popup.maxAppVersion) {
      matchers.push(`version ${popup.minAppVersion ?? '*'} - ${popup.maxAppVersion ?? '*'}`);
    }
    if (popup.countries?.length) {
      matchers.push(popup.countries.join(','));
    }
    if (popup.levelConditions?.length) {
      matchers.push(`level ${popup.levelConditions.map((c) => `${c.op}${c.value}`).join(' & ')}`);
    } else if (popup.minLevel != null || popup.maxLevel != null) {
      matchers.push(`level ${popup.minLevel ?? '*'} - ${popup.maxLevel ?? '*'}`);
    }
    if (popup.payerStatus !== 'ALL') {
      matchers.push(popup.payerStatus === 'PAYER' ? 'PAU' : 'NPAU');
    }
    if (popup.vipStatus !== 'ALL') {
      matchers.push(popup.vipStatus);
    }
    return matchers.length ? matchers.join(' · ') : 'Everyone';
  };

  const saving = createLoading || updateLoading;

  return (
    <>
      {statusMessage && (
        <Alert variant={statusVariant} onClose={() => setStatusMessage(null)} dismissible>
          {statusMessage}
        </Alert>
      )}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Custom Popups</span>
          <Button size="sm" onClick={onNewPopup}>
            New Popup
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Daily Cap</th>
                  <th>CTA</th>
                  <th>Audience</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {popups.map((popup) => (
                  <tr key={popup.id}>
                    <td>{popup.name}</td>
                    <td>{popup.place}</td>
                    <td>
                      <Badge variant={popup.enabled ? 'success' : 'secondary'}>
                        {popup.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </td>
                    <td>{popup.dailyCap}/day</td>
                    <td>{popup.buttonAction === 'OPEN_URL' ? popup.buttonUrl : 'Dismiss'}</td>
                    <td>{describeMatchers(popup)}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => onEditPopup(popup)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={deleteLoading}
                        onClick={() => onDeletePopup(popup)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
                {!popups.length && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      No custom popups yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showEditorModal} onHide={() => setShowEditorModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{isDraft ? 'New Custom Popup' : `Edit: ${name}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form>
                <div style={sectionStyle(0)}>
                  <h6>Basics</h6>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control value={name} onChange={(event) => setName(event.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Location</Form.Label>
                        <Form.Control
                          as="select"
                          value={place}
                          onChange={(event) => setPlace(event.currentTarget.value as PopupPlacement)}
                        >
                          {PLACEMENTS.map((placement) => (
                            <option key={placement} value={placement}>
                              {placement}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Enabled</Form.Label>
                        <Form.Check
                          type="switch"
                          id="custom-popup-enabled"
                          checked={enabled}
                          onChange={(event) => setEnabled(event.currentTarget.checked)}
                          label={enabled ? 'On' : 'Off'}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div style={sectionStyle(1)}>
                  <h6>Content</h6>

                  <Form.Group className="mb-3">
                    <Form.Label>Title (JSON per locale)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={titleTextsJson}
                      onChange={(event) => setTitleTextsJson(event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Content (JSON per locale)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={bodyTextsJson}
                      onChange={(event) => setBodyTextsJson(event.target.value)}
                    />
                  </Form.Group>
                </div>

                <div style={sectionStyle(2)}>
                  <h6>Buttons</h6>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Button Text (JSON per locale)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={buttonTextsJson}
                          onChange={(event) => setButtonTextsJson(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Button Action</Form.Label>
                        <Form.Control
                          as="select"
                          value={buttonAction}
                          onChange={(event) => setButtonAction(event.currentTarget.value as ButtonAction)}
                        >
                          <option value="DISMISS">Dismiss popup</option>
                          <option value="OPEN_URL">Open web page</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Button URL</Form.Label>
                        <Form.Control
                          value={buttonUrl}
                          disabled={buttonAction !== 'OPEN_URL'}
                          placeholder="https://..."
                          onChange={(event) => setButtonUrl(event.target.value)}
                        />
                        <Form.Check
                          type="checkbox"
                          id="custom-popup-survey-link"
                          className="mt-1"
                          checked={isSurveyLink}
                          disabled={buttonAction !== 'OPEN_URL'}
                          onChange={(event) => setIsSurveyLink(event.currentTarget.checked)}
                          label="Survey link (game appends userID & country params)"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Close Button Text (JSON per locale)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={secondaryButtonTextsJson}
                          onChange={(event) => setSecondaryButtonTextsJson(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div style={sectionStyle(3)}>
                  <h6>Appearance</h6>

                  <Row>
                    <Col md={7}>
                      <Form.Group className="mb-3">
                        <Form.Label>Background Image (optional, shown behind the text with a dark overlay)</Form.Label>
                        <div className="d-flex align-items-center mb-1">
                          <label htmlFor="custom-popup-bg-upload" className="btn btn-sm btn-outline-primary mb-0 mr-2">
                            {uploadLoading ? 'Uploading…' : 'Upload from PC'}
                          </label>
                          <input
                            id="custom-popup-bg-upload"
                            type="file"
                            accept="image/*"
                            hidden
                            disabled={uploadLoading}
                            onChange={onBackgroundFileSelected}
                          />
                          {backgroundImageUrl.trim() && (
                            <Button size="sm" variant="outline-danger" onClick={() => setBackgroundImageUrl('')}>
                              Remove
                            </Button>
                          )}
                        </div>
                        <Form.Control
                          value={backgroundImageUrl}
                          placeholder="https://... (or use Upload from PC)"
                          onChange={(event) => setBackgroundImageUrl(event.target.value)}
                        />
                        {uploadedAssets.length > 0 && (
                          <>
                            <div className="text-muted small mt-2 mb-1">Or pick a previously uploaded image:</div>
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                overflowX: 'auto',
                                paddingBottom: 4,
                              }}
                            >
                              {uploadedAssets.map((asset) => {
                                const isSelected = backgroundImageUrl.trim() === asset.url;
                                return (
                                  <button
                                    key={asset.url}
                                    type="button"
                                    title={asset.filename}
                                    onClick={() => setBackgroundImageUrl(isSelected ? '' : asset.url)}
                                    style={{
                                      width: 54,
                                      height: 88,
                                      flexShrink: 0,
                                      padding: 0,
                                      borderRadius: 6,
                                      border: isSelected ? '3px solid #7a4fd0' : '1px solid #ced4da',
                                      backgroundImage: `url(${asset.url})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      cursor: 'pointer',
                                      outline: 'none',
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>UI Theme (colors)</Form.Label>
                        <Form.Control
                          as="select"
                          value={uiTheme}
                          onChange={(event) => setUiTheme(event.currentTarget.value as PopupUiTheme)}
                        >
                          <option value="DEFAULT">Default (standard colors)</option>
                          <option value="VINYL">Vinyl (follows the player&apos;s active vinyl)</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div style={sectionStyle(4)}>
                  <h6>Don&apos;t Show Again</h6>

                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>Suppresses For</Form.Label>
                        <Form.Control
                          as="select"
                          value={dontShowAgainPolicy}
                          onChange={(event) => setDontShowAgainPolicy(event.currentTarget.value as DontShowAgainPolicy)}
                        >
                          <option value="SESSION">Rest of the session</option>
                          <option value="DAY">Rest of the day</option>
                          <option value="FOREVER">Forever</option>
                          <option value="NONE">None (hide the checkbox)</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={7}>
                      <Form.Group className="mb-3">
                        <Form.Label>Checkbox Label (JSON per locale)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={dontShowAgainTextsJson}
                          disabled={dontShowAgainPolicy === 'NONE'}
                          onChange={(event) => setDontShowAgainTextsJson(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div style={sectionStyle(5)}>
                  <h6>Audience matchers (leave empty to match everyone)</h6>

                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Daily Cap (shows/day)</Form.Label>
                        <Form.Control value={dailyCap} onChange={(event) => setDailyCap(event.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Platform</Form.Label>
                        <Form.Control
                          as="select"
                          value={platform}
                          onChange={(event) => setPlatform(event.currentTarget.value as PopupPlatform)}
                        >
                          <option value="ALL">Both (iOS &amp; Android)</option>
                          <option value="IOS">iOS only</option>
                          <option value="ANDROID">Android only</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Countries (ISO, comma)</Form.Label>
                        <Form.Control
                          value={countriesText}
                          placeholder="US, FR, DE"
                          onChange={(event) => setCountriesText(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <ConditionListEditor
                        label="App Version Conditions"
                        rows={versionConditionRows}
                        placeholder="003.013.000"
                        onChange={setVersionConditionRows}
                      />
                    </Col>
                    <Col md={6}>
                      <ConditionListEditor
                        label="Player Level Conditions"
                        rows={levelConditionRows}
                        placeholder="10"
                        onChange={setLevelConditionRows}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>PAU / NPAU</Form.Label>
                        <Form.Control
                          as="select"
                          value={payerStatus}
                          onChange={(event) => setPayerStatus(event.currentTarget.value as PayerStatus)}
                        >
                          <option value="ALL">Everyone</option>
                          <option value="PAYER">PAU (has paid)</option>
                          <option value="NON_PAYER">NPAU (never paid)</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>VIP Status</Form.Label>
                        <Form.Control
                          as="select"
                          value={vipStatus}
                          onChange={(event) => setVipStatus(event.currentTarget.value as VipStatus)}
                        >
                          <option value="ALL">Everyone</option>
                          <option value="VIP">VIP (active subscriber)</option>
                          <option value="NON_VIP">Non-VIP</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Target User IDs (one per line, empty = all users)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={targetUserIdsText}
                      onChange={(event) => setTargetUserIdsText(event.target.value)}
                    />
                  </Form.Group>
                </div>
              </Form>
            </Col>
            <Col md={4}>
              <div style={{ position: 'sticky', top: 0 }}>
                <h6 className="text-center mb-2">In-game preview</h6>
                <PopupPreview
                  titleJson={titleTextsJson}
                  bodyJson={bodyTextsJson}
                  buttonJson={buttonTextsJson}
                  secondaryJson={secondaryButtonTextsJson}
                  dontShowAgainJson={dontShowAgainTextsJson}
                  dontShowAgainPolicy={dontShowAgainPolicy}
                  uiTheme={uiTheme}
                  backgroundImageUrl={backgroundImageUrl}
                />
                {uiTheme === 'VINYL' && (
                  <div className="text-muted small text-center mt-1">
                    Vinyl theme follows the player&apos;s active vinyl in-game; the preview shows the standard vinyl
                    colors.
                  </div>
                )}
                <div className="text-muted small text-center mt-2">
                  Approximation of the in-game popup (en texts). Long content scrolls like in the game.
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditorModal(false)}>
            Cancel
          </Button>
          <Button onClick={onSavePopup} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomPopups;
